import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';

const emptyUnit = { code: '', name: '', active: true };

export const UnitsManager = () => {
  const confirm = useConfirm();
  const [units, setUnits] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentUnit, setCurrentUnit] = useState(null);

  useEffect(() => { loadUnits(); }, []);

  const loadUnits = async () => {
    try {
      const response = await api.getUnits();
      setUnits(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento unità');
    }
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    try {
      if (currentUnit.id) {
        await api.updateUnit(currentUnit.id, currentUnit);
        toast.success('Unità aggiornata');
      } else {
        await api.createUnit(currentUnit);
        toast.success('Unità creata');
      }
      setDialogOpen(false);
      setCurrentUnit(null);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    const ok = await confirm({
      title: 'Elimina unità di misura',
      description: 'Stai per eliminare definitivamente questa unità. Le unità in uso da articoli non possono essere eliminate.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteUnit(unitId);
      toast.success('Unità eliminata');
      setDialogOpen(false);
      setCurrentUnit(null);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  return (
    <Card data-testid="units-manager">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Unità di Misura</CardTitle>
          <p className="text-sm text-slate-600 mt-1">
            Gestisci le unità di misura disponibili per gli articoli
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setCurrentUnit(emptyUnit)} data-testid="create-unit-btn">
              <Plus size={18} className="mr-2" />
              Nuova Unità
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{currentUnit?.id ? 'Modifica Unità' : 'Nuova Unità'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveUnit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="unit_code">Codice *</Label>
                <Input
                  id="unit_code"
                  value={currentUnit?.code || ''}
                  onChange={(e) => setCurrentUnit({ ...currentUnit, code: e.target.value })}
                  placeholder="Es: ora, pz, mq"
                  required
                  data-testid="unit-code-input"
                />
                <p className="text-xs text-slate-500">Codice breve dell&apos;unità (es: ora, pz, mq, ml, kg)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit_name">Nome Completo *</Label>
                <Input
                  id="unit_name"
                  value={currentUnit?.name || ''}
                  onChange={(e) => setCurrentUnit({ ...currentUnit, name: e.target.value })}
                  placeholder="Es: Ora, Pezzo, Metro Quadro"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit_status">Stato *</Label>
                <Select
                  value={(currentUnit?.active === false) ? 'suspended' : 'active'}
                  onValueChange={(value) => setCurrentUnit({ ...currentUnit, active: value === 'active' })}
                >
                  <SelectTrigger data-testid="unit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Attivo</SelectItem>
                    <SelectItem value="suspended">Sospeso</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Le unità sospese non sono selezionabili nei bollettini</p>
              </div>
              <div className="flex justify-between items-center pt-2">
                {currentUnit?.id ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDeleteUnit(currentUnit.id)}
                    className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    data-testid="dialog-delete-unit-btn"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Elimina
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit">Salva</Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {units.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Nessuna unità di misura configurata</p>
            <p className="text-sm text-slate-500 mt-2">Aggiungi le unità necessarie per i tuoi articoli</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Codice</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-700">Nome</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-700">Stato</th>
                </tr>
              </thead>
              <tbody>
                {units.map(unit => (
                  <tr
                    key={unit.id}
                    className="border-b hover:bg-secondary cursor-pointer"
                    onClick={() => { setCurrentUnit(unit); setDialogOpen(true); }}
                    data-testid={`unit-row-${unit.id}`}
                  >
                    <td className="p-4">
                      <span className="font-mono font-medium text-slate-900">{unit.code}</span>
                    </td>
                    <td className="p-4 text-slate-700">{unit.name}</td>
                    <td className="p-4 text-center">
                      {unit.active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          Attivo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          Sospeso
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UnitsManager;
